/**
 * The help library's sections (TMMAPP-0142 §I): the server serves each section's key and how many
 * articles it holds; the app labels the keys and orders them as the library plans them
 * (`docs/help/README.md`). An unknown key is "More help" and goes last; an empty section is left out.
 */
import type { HelpSection } from '@/api/help';

/** The library's section keys in its own order, each with its catalog label. */
const SECTION_LABELS: ReadonlyArray<readonly [key: string, labelKey: string]> = [
  ['getting-started', 'support.help.section.gettingStarted'],
  ['accounts', 'support.help.section.accounts'],
  ['importing', 'support.help.section.importing'],
  ['payees', 'support.help.section.payees'],
  ['transfers-and-paybacks', 'support.help.section.transfersAndPaybacks'],
  ['virtual-accounts-and-envelopes', 'support.help.section.virtualAccountsAndEnvelopes'],
  ['recurring', 'support.help.section.recurring'],
  ['cash-flow', 'support.help.section.cashFlow'],
  ['money-meetings', 'support.help.section.moneyMeetings'],
  ['ai', 'support.help.section.ai'],
  ['subscription-and-billing', 'support.help.section.subscriptionAndBilling'],
  ['settings-and-your-data', 'support.help.section.settingsAndYourData'],
  ['help', 'support.help.section.help'],
];

const UNKNOWN_SECTION = 'support.help.section.more';

const ORDER = new Map(SECTION_LABELS.map(([key], index) => [key, index]));
const LABELS = new Map(SECTION_LABELS);

/** A section's catalog key: its own label, else "More help". */
export function sectionLabelKey(key: string | null | undefined): string {
  return (key !== null && key !== undefined ? LABELS.get(key) : undefined) ?? UNKNOWN_SECTION;
}

/** The sections to browse: in the library's order, unknown ones last, empty ones left out. */
export function orderedSections(sections: readonly HelpSection[]): HelpSection[] {
  const rank = (key: string) => ORDER.get(key) ?? SECTION_LABELS.length;
  return sections
    .filter((section) => section.articleCount > 0)
    .map((section, index) => ({ section, index }))
    .sort((a, b) => rank(a.section.key) - rank(b.section.key) || a.index - b.index)
    .map(({ section }) => section);
}
